import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import PageShell from '../components/site/PageShell';
import { images } from '../config/esanzaContent';
import { useBlogPost } from '../hooks/useBlogPosts';

const BlogPostPage: React.FC = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const { post, loading } = useBlogPost(slug);

  if (!slug) return <Navigate to="/blog" replace />;

  if (loading) {
    return (
      <PageShell>
        <main className="min-h-screen bg-[#f7f9f4] px-5 pt-36 text-center text-[#102016]">
          <p className="text-sm font-bold text-[#596959]">{t('site.blog.loading_story', 'Loading story...')}</p>
        </main>
      </PageShell>
    );
  }

  if (!post) {
    return (
      <PageShell>
        <main className="min-h-screen bg-[#f7f9f4] px-5 pt-36 text-center text-[#102016]">
          <h1 className="text-3xl font-black">{t('site.blog.not_found', 'Story not found')}</h1>
          <Link to="/blog" className="mt-6 inline-flex rounded-full bg-[#14532d] px-6 py-3 text-sm font-extrabold text-white">
            {t('site.blog.back', 'Back to stories')}
          </Link>
        </main>
      </PageShell>
    );
  }

  return (
    <>
      <SEO
        title={`${post.title} | eSanza`}
        description={post.excerpt || post.body.slice(0, 150)}
        imagePath={post.image_url || images.tree}
        keywords={(post.tags || []).join(', ')}
        type="article"
      />
      <PageShell>
        <article className="bg-[#f7f9f4] text-[#102016]">
          <section className="relative overflow-hidden px-5 pb-20 pt-32 lg:px-8">
            <img src={post.image_url || images.tree} alt="" className="absolute inset-0 h-full w-full object-cover opacity-35" />
            <div className="absolute inset-0 bg-[linear-gradient(90deg,#06150e_0%,rgba(6,21,14,.9)_55%,rgba(6,21,14,.55)_100%)]" />
            <div className="relative mx-auto max-w-4xl">
              <Link to="/blog" className="text-sm font-extrabold text-[#d4af37] hover:text-white">{t('site.blog.back', 'Back to stories')}</Link>
              <div className="mt-8 flex flex-wrap gap-2">
                <span className="rounded-full bg-[#d4af37]/15 px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em] text-[#f3d978]">{post.category || t('site.blog.field_story', 'Field Story')}</span>
                {post.featured && <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em] text-white">{t('site.blog.featured', 'Featured')}</span>}
              </div>
              <h1 className="mt-5 text-4xl font-black leading-[1.03] tracking-tight text-white md:text-6xl">{post.title}</h1>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-white/70">{post.excerpt}</p>
              <p className="mt-6 text-sm font-semibold text-white/50">
                {post.author || t('site.blog.author', 'eSanza Team')}
                {post.published_at ? ` • ${new Date(post.published_at).toLocaleDateString()}` : ''}
              </p>
            </div>
          </section>

          <section className="px-5 py-20 lg:px-8">
            <div className="mx-auto max-w-3xl">
              <div className="rounded-lg border border-[#dbe5d2] bg-white p-7 shadow-sm md:p-10">
                {post.body.split('\n').filter(Boolean).map((paragraph, index) => (
                  <p key={index} className="mb-5 text-base leading-8 text-[#344536] last:mb-0">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </section>
        </article>
      </PageShell>
    </>
  );
};

export default BlogPostPage;
